import type { Language } from '@expenditure/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthProvider';
import { Segmented } from '../components/ui';
import { api } from '../lib/api';
import { haptic, requestBotMessages } from '../lib/telegram';
import { Icon, type IconName } from '../components/Icon';
import { Logo } from '../components/Logo';

export function WelcomePage({ onDone }: { onDone: () => void }) {
  const { t, i18n } = useTranslation();
  const { setUser } = useAuth();
  const [saving, setSaving] = useState(false);

  const chooseLanguage = async (language: Language) => {
    haptic.select();
    void i18n.changeLanguage(language);
    document.documentElement.lang = language;
    try {
      setUser((await api.updateMe({ language })).user);
    } catch {
      // Not critical: the choice can be changed later in Profile.
    }
  };

  const features = [
    { icon: 'groups', title: t('welcome.feature1Title'), body: t('welcome.feature1Body') },
    { icon: 'divide', title: t('welcome.feature2Title'), body: t('welcome.feature2Body') },
    { icon: 'swap', title: t('welcome.feature3Title'), body: t('welcome.feature3Body') },
  ] satisfies Array<{ icon: IconName; title: string; body: string }>;

  return (
    <main className="welcome app">
      <div className="stack" style={{ alignItems: 'flex-start', gap: 16 }}>
        <Logo size={72} className="logo-mark" />
        <div>
          <h1 className="page-title" style={{ fontSize: 30 }}>
            {t('welcome.title')}
          </h1>
          <p className="muted" style={{ margin: '6px 0 0', fontSize: 17 }}>
            {t('app.tagline')}
          </p>
        </div>
      </div>

      <div className="field">
        <span className="field-label">{t('welcome.language')}</span>
        <Segmented<Language>
          value={i18n.language === 'km' ? 'km' : 'en'}
          onChange={(language) => void chooseLanguage(language)}
          options={[
            { value: 'km', label: 'ខ្មែរ' },
            { value: 'en', label: 'English' },
          ]}
        />
      </div>

      <div className="card stack" style={{ gap: 18 }}>
        {features.map((f) => (
          <div className="feature" key={f.title}>
            <span className="icon-tile icon-tile--ink" aria-hidden>
              <Icon name={f.icon} size={20} />
            </span>
            <div>
              <div className="list-title">{f.title}</div>
              <div className="muted small">{f.body}</div>
            </div>
          </div>
        ))}
      </div>

      <button
        className="btn btn--primary btn--block"
        style={{ marginTop: 'auto' }}
        disabled={saving}
        onClick={async () => {
          setSaving(true);
          haptic.success();
          // Telegram shows a native "allow messages" prompt; with it the bot can
          // send payment requests and updates. Declining is fine.
          if (await requestBotMessages()) {
            await api.updateMe({ botAllowed: true }).then(({ user }) => setUser(user)).catch(() => undefined);
          }
          onDone();
        }}
      >
        {t('welcome.getStarted')}
      </button>
    </main>
  );
}
